import React, { useState } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import { updateProfile, profileNameExists } from '../../utils/profileManager';
import { profileColors } from '../../data/avatars';
import { playSound } from '../../utils/soundManager';
import AvatarPicker from './AvatarPicker';

const ProfileEditor = ({ profile, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: profile.name,
    age: profile.age,
    avatar: profile.avatar,
    color: profile.color
  });
  const [errors, setErrors] = useState({});
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Please enter a name';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (formData.name.trim().length > 20) {
      newErrors.name = 'Name must be less than 20 characters';
    } else if (profileNameExists(formData.name, profile.id)) {
      newErrors.name = 'This name is already taken';
    }

    if (!formData.age) {
      newErrors.age = 'Please select an age';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validate()) {
      playSound('success');
      updateProfile(profile.id, formData);
      onSave();
    } else {
      playSound('error');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAvatarSelect = (avatar) => {
    handleInputChange('avatar', avatar.emoji);
    setShowAvatarPicker(false);
    playSound('click');
  };

  const handleColorSelect = (color) => {
    handleInputChange('color', color.color);
    playSound('click');
  };

  if (showAvatarPicker) {
    return (
      <AvatarPicker
        selectedAvatar={formData.avatar}
        onSelect={handleAvatarSelect}
        onBack={() => setShowAvatarPicker(false)}
      />
    );
  }

  return (
    <div className="profile-editor-container">
      <div className="profile-editor-content animate-fadeIn">
        <div className="profile-editor-header">
          <button
            className="back-button hover-scale btn-press"
            onClick={onCancel}
          >
            <ArrowLeft size={24} />
          </button>
          <h2>Edit Profile</h2>
          <div></div>
        </div>

        <div className="profile-editor-body">
          <div className="form-section">
            <h3>👤 Name</h3>
            <input
              type="text"
              className={`profile-input ${errors.name ? 'input-error' : ''}`}
              placeholder="Enter name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              maxLength={20}
            />
            {errors.name && (
              <p className="error-message animate-shake">{errors.name}</p>
            )}
          </div>

          <div className="form-section">
            <h3>🎂 Age</h3>
            <div className="age-selector">
              {[...Array(13)].map((_, i) => {
                const age = i + 3;
                return (
                  <button
                    key={age}
                    className={`age-button hover-scale btn-press ${
                      formData.age === age ? 'selected' : ''
                    }`}
                    onClick={() => handleInputChange('age', age)}
                  >
                    {age}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="form-section">
            <h3>🎨 Avatar</h3>
            <div className="avatar-preview-container">
              <div
                className="avatar-preview hover-scale"
                style={{ background: formData.color }}
                onClick={() => setShowAvatarPicker(true)}
              >
                <span className="avatar-preview-emoji">{formData.avatar}</span>
              </div>
              <button
                className="change-avatar-button hover-lift btn-press"
                onClick={() => setShowAvatarPicker(true)}
              >
                Change Avatar
              </button>
            </div>
          </div>

          <div className="form-section">
            <h3>🌈 Color</h3>
            <div className="color-selector">
              {profileColors.map((color) => (
                <button
                  key={color.id}
                  className={`color-button hover-scale ${
                    formData.color === color.color ? 'selected' : ''
                  }`}
                  style={{ background: color.color }}
                  onClick={() => handleColorSelect(color)}
                  title={color.name}
                >
                  {formData.color === color.color && '✓'}
                </button>
              ))}
            </div>
          </div>

          <div className="profile-preview">
            <h3>Preview</h3>
            <div className="preview-card">
              <div
                className="preview-avatar"
                style={{ background: formData.color }}
              >
                <span>{formData.avatar}</span>
              </div>
              <div className="preview-info">
                <h4>{formData.name}</h4>
                <p>Age {formData.age}</p>
              </div>
            </div>
          </div>

          <div className="profile-editor-actions">
            <button
              className="cancel-button hover-lift btn-press"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              className="save-button hover-lift btn-press"
              onClick={handleSave}
            >
              <Save size={20} />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditor;
